/**
 * Wishlist stored in local storage, keyed by product handle.
 *
 * Global rather than bundled with the product card, because the heart also
 * appears on the product page — where no card is necessarily rendered. Keeping
 * the behaviour in one place means every heart in the theme shares one source
 * of truth instead of depending on which components happen to be on the page.
 *
 * Buttons render pressed-out and are corrected on load: the saved list is
 * per-browser, so baking it into the HTML would make every page uncacheable
 * for one visitor's benefit.
 *
 * Delegated from the document so hearts added later — by the recommendations
 * fetch, or the wishlist page itself — are covered without re-binding.
 */
(function () {
  const WISHLIST_KEY = 'wishlist';

  function readWishlist() {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(handles) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
    } catch (error) {
      // Private browsing or a full quota; the toggle simply won't persist.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles } }));
  }

  /**
   * Makes the list portable across devices.
   *
   * Being straight about the ceiling here: a theme cannot write to customer
   * metafields, so there is no way from this file to give a logged-in shopper
   * a list that follows their account — that needs an app with Admin API
   * access. What a theme *can* do is put the list in a URL, which covers the
   * case people actually hit: saving on a phone and buying on a laptop.
   *
   * Handles are merged into the existing list rather than replacing it, so
   * opening a shared link never silently destroys what you already saved.
   */
  function importWishlistFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const shared = params.get('wishlist');
    if (!shared) return;

    const incoming = shared
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
    if (!incoming.length) return;

    const merged = Array.from(new Set(readWishlist().concat(incoming)));
    writeWishlist(merged);

    // Drop the parameter so a refresh, or a link the shopper then shares on,
    // does not carry someone else's list around forever.
    params.delete('wishlist');
    const query = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? '?' + query : '') + window.location.hash
    );
  }

  /**
   * Builds the link for the current list. Exposed on `window` so the wishlist
   * page can offer a share button without duplicating the encoding.
   */
  function wishlistShareUrl() {
    const handles = readWishlist();
    if (!handles.length) return '';
    return (
      window.location.origin +
      (window.__wishlistPath || window.location.pathname) +
      '?wishlist=' +
      handles.map(encodeURIComponent).join(',')
    );
  }

  window.wishlistShareUrl = wishlistShareUrl;

  function syncWishlistButtons(root) {
    const saved = readWishlist();

    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      button.setAttribute('aria-pressed', String(saved.includes(button.dataset.handle)));
    });

    /**
     * The header badge. Hidden at zero rather than showing "0", which reads as
     * a broken counter, and marked aria-hidden in the markup because the link
     * beside it already announces itself as the wishlist.
     */
    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      badge.textContent = saved.length > 0 ? String(saved.length) : '';
      badge.hidden = saved.length === 0;
    });
  }

  // For markup injected after load, e.g. the quick view.
  window.syncWishlistButtons = syncWishlistButtons;

  if (window.__wishlistBound) return;
  window.__wishlistBound = true;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;

    event.preventDefault();

    const handle = button.dataset.handle;
    const saved = readWishlist();
    const index = saved.indexOf(handle);

    if (index > -1) {
      saved.splice(index, 1);
    } else {
      saved.push(handle);
    }

    writeWishlist(saved);
    syncWishlistButtons();
  });

  document.addEventListener('DOMContentLoaded', () => syncWishlistButtons());

  // Another tab may have changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) syncWishlistButtons();
  });

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', () => syncWishlistButtons());

  // Before the first sync, so a shared link's hearts are already correct on
  // the very first paint rather than filling in a frame later.
  importWishlistFromUrl();

  syncWishlistButtons();
})();
